/**
 * Permissions & RBAC utilities for Boat Trader
 * Centralized role and permission checking
 */

import { Dealer } from '../types';

export type Role = 'admin' | 'dealer' | 'public';

export function getUserRole(user: Dealer | null): Role {
  if (!user) return 'public';
  if (user.is_admin === true) return 'admin';
  return 'dealer';
}

export interface PermissionSettings {
  dealer_can_prenota: boolean;
  dealer_can_trattativa: boolean;
  dealer_can_delete: boolean;
  dealer_can_edit_profile: boolean;
  dealer_can_export: boolean;
  public_can_view_dealer_price: boolean;
  public_can_view_contacts: boolean;
  public_can_chat: boolean;
}

// In-memory cache (session-only, cleared on reload)
let permissionSettingsCache: PermissionSettings | null = null;

// Default settings
const defaultSettings: PermissionSettings = {
  dealer_can_prenota: true,
  dealer_can_trattativa: true,
  dealer_can_delete: false,
  dealer_can_edit_profile: false,
  dealer_can_export: false,
  public_can_view_dealer_price: false,
  public_can_view_contacts: false,
  public_can_chat: false,
};

/**
 * Get permission settings (synchronous, from in-memory cache or defaults).
 * For freshest data, this will be re-fetched by the background fetch on app start.
 */
export function getPermissionSettings(): PermissionSettings {
  if (permissionSettingsCache !== null) {
    return permissionSettingsCache;
  }
  return defaultSettings;
}

/**
 * Background fetch to populate permission settings cache.
 * Call this once on app startup if needed.
 */
export async function fetchPermissionSettingsIfNeeded(): Promise<void> {
  if (permissionSettingsCache !== null) return;
  
  try {
    const { default: api } = await import('./api');
    const response = await api.get('/settings');
    if (response?.data?.data) {
      const settings = response.data.data;
      permissionSettingsCache = {
        dealer_can_prenota: Boolean(settings.dealer_can_prenota) || false,
        dealer_can_trattativa: Boolean(settings.dealer_can_trattativa) || false,
        dealer_can_delete: Boolean(settings.dealer_can_delete) || false,
        dealer_can_edit_profile: Boolean(settings.dealer_can_edit_profile) || false,
        dealer_can_export: Boolean(settings.dealer_can_export) || false,
        public_can_view_dealer_price: Boolean(settings.public_can_view_dealer_price) || false,
        public_can_view_contacts: Boolean(settings.public_can_view_contacts) || false,
        public_can_chat: Boolean(settings.public_can_chat) || false,
      };
    }
  } catch {
    // Silently fallback to defaults
  }
}

// No-op clear (not using persistent storage)
export function clearPermissionSettingsCache(): void {
  permissionSettingsCache = null;
}
